import React, { useState } from "react";
import { Icon } from "../icons";

/**
 * Knowledge Hub (design of 2026-10-29): four short sections behind tabs, a
 * search across all of them, and a daily routine the MC can tick off.
 */

const SECTIONS = [
  { id: "start", label: "Start here", blurb: "The big picture in 30 seconds." },
  {
    id: "journey",
    label: "Job journey",
    blurb: "The six tabs inside every job card, and the board columns each job moves through.",
  },
  { id: "daily", label: "Daily routine", blurb: "A simple order for working your day." },
  {
    id: "answers",
    label: "Client answers",
    blurb:
      "Opens the live page on roadwaymoving.com in a new tab — good for reading a client the exact wording or forwarding the link.",
  },
];

const Flow = ({ steps }) => (
  <div className="kh-flow">
    {steps.map((s, i) => (
      <React.Fragment key={s}>
        <span className="kh-chip">{s}</span>
        {i < steps.length - 1 ? <span className="kh-arrow">→</span> : null}
      </React.Fragment>
    ))}
  </div>
);

const Step = ({ n, title, desc, who }) => (
  <div className="kh-card">
    <div className="kh-num">{n}</div>
    <div>
      <div className="kh-t">{title}</div>
      <div className="kh-d">{desc}</div>
      <div className="kh-who">{who}</div>
    </div>
  </div>
);

const LinkCard = ({ title, desc, url }) => (
  <a className="kh-link" href={url} target="_blank" rel="noopener noreferrer">
    <div>
      <div className="kh-t">{title}</div>
      <div className="kh-d">{desc}</div>
    </div>
    <Icon name="external" />
  </a>
);

const Term = ({ title, desc }) => (
  <div className="kh-card">
    <div>
      <div className="kh-t">{title}</div>
      <div className="kh-d">{desc}</div>
    </div>
  </div>
);

const step = (n, title, desc, who) => ({ text: `${title} ${desc} ${who}`, node: <Step n={n} title={title} desc={desc} who={who} /> });
const link = (title, desc, url) => ({ text: `${title} ${desc}`, node: <LinkCard title={title} desc={desc} url={url} /> });
const term = (title, desc) => ({ text: `${title} ${desc}`, node: <Term title={title} desc={desc} /> });
const task = (id, title, desc) => ({ text: `${title} ${desc}`, task: { id, title, desc } });

export const DAILY_TASKS = ["d1", "d2", "d3", "d4", "d5"];

/** Every section's groups of items; `text` is what the search looks through. */
const DATA = {
  start: [
    {
      group: "How work flows",
      items: [
        {
          text: "flow jobs to be confirmed active moves post move",
          node: (
            <>
              <Flow steps={["Jobs to be Confirmed", "Active Moves", "Post Move"]} />
              <div className="kh-grid3">
                <Term title="Jobs to be Confirmed" desc="Local Moves and Long Distance Jobs." />
                <Term title="Active Moves" desc="Moves in Progress and Delivery Day in Progress." />
                <Term title="Post Move" desc="The job closes out here." />
              </div>
            </>
          ),
        },
      ],
    },
    {
      group: "Good to know",
      items: [
        {
          text: "escalations reminders travel with the job group chat",
          node: (
            <div className="kh-note">
              <strong>Context travels with the job.</strong> Escalations and reminders follow the job the whole way
              and post automatically to the Ops/Sales group chat, so you never have to re-explain context to another
              team.
            </div>
          ),
        },
        {
          text: "six tabs job card",
          node: (
            <div className="kh-note">
              <strong>Every job has the same journey.</strong> Open the <em>Job journey</em> section to see what each
              of the six tabs is for.
            </div>
          ),
        },
      ],
    },
  ],
  journey: [
    {
      group: "The six tabs inside every job card",
      items: [
        step(1, "Summary", "What Sales and Zoho already captured.", "Read first"),
        step(2, "Introduction Call", "Your first outreach: logistics, access, payment method.", "Before the move"),
        step(3, "Confirmation Call", "Lock the details and flag risks before the move.", "Before the move"),
        step(
          4,
          "In Transit / Delivery Day",
          "Long distance: track the shipment to warehouse-received and delivery. Local moves use In Progress for the day of the move.",
          "Move time",
        ),
        step(5, "Post Move", "VIP closing call or automated follow-up.", "After the move"),
        step(6, "Notes Hub", "Every note left across the whole journey, in one place.", "Anytime"),
      ],
    },
    {
      group: "Board columns: Local Moves",
      items: [
        {
          text: "local moves columns daily assignments revised confirmations weekly assignments confirmed intro call completed",
          node: <Flow steps={["Daily Assignments", "Revised Confirmations", "Weekly Assignments / Confirmed", "Intro Call Completed"]} />,
        },
      ],
    },
    {
      group: "Board columns: Long Distance Jobs",
      items: [
        {
          text: "long distance columns to be confirmed in transit sit local confirmed delivery day",
          node: <Flow steps={["To Be Confirmed", "In Transit", "SIT Local", "Confirmed / Delivery Day"]} />,
        },
      ],
    },
  ],
  daily: [
    {
      group: "Work your day in this order",
      items: [
        task("d1", "Start on Landing", "Check the priority queue, open escalations and anything overdue."),
        task("d2", "Work Local Moves", "Go left to right. Each column is the next step a job needs."),
        task("d3", "Work Long Distance Jobs", "Same idea: left to right through the columns."),
        task("d4", "Clear Needs Attention", "Every missed call, unanswered text or email and open escalation. Clear it before end of day."),
        task("d5", "Check Reminders", "Overdue and due-today items across every job."),
      ],
    },
  ],
  answers: [
    {
      group: "Insurance and coverage",
      items: [
        link("What is COI?", "Certificate of insurance services.", "https://www.roadwaymoving.com/certificate-of-insurance-services/"),
        link("What is full replacement coverage?", "What full replacement coverage includes.", "https://www.roadwaymoving.com/fvp/"),
      ],
    },
    {
      group: "Services and pricing",
      items: [
        link("What is shuttle?", "Moving FAQ: when and why a shuttle is used.", "https://www.roadwaymoving.com/moving-faq/"),
        link("How do we charge for storage?", "Storage pricing and terms.", "https://www.roadwaymoving.com/storage/"),
      ],
    },
    {
      group: "Day of the move",
      items: [link("Tipping the movers", "Client tipping guide.", "https://www.roadwaymoving.com/tipping-guide/")],
    },
    {
      group: "Key terms to explain",
      items: [
        term(
          "COI — Certificate of Insurance",
          "Proof of insurance a building requires before movers can work. Confirm early whether pickup and/or delivery buildings need one, and get the building's exact requirements so we can issue it in time.",
        ),
        term(
          "Full Replacement Value Protection (FVP)",
          "If an item is lost or damaged, it is repaired or replaced at full value rather than the default 60¢-per-pound liability.",
        ),
        term("Shuttle", "A smaller vehicle used when the truck cannot reach the door. It adds time and cost, so flag it whenever access looks tight."),
        term("Storage billing", "Storage is billed monthly by volume (cubic feet). Make sure the client knows the monthly charge and how re-delivery is scheduled."),
        term("Delivery window (Long Distance)", "The date range within which an LD shipment will be delivered — not a single guaranteed day."),
        term("Tipping", "Always the client's choice and never required. Share the tipping guide if asked; never quote a set amount."),
      ],
    },
  ],
};

export const KnowledgeHubPage = () => {
  const [tab, setTab] = useState("start");
  const [query, setQuery] = useState("");
  const [checks, setChecks] = useState({});
  const q = query.trim().toLowerCase();

  const renderItem = (item, i) =>
    item.task ? (
      <label className={`kh-task${checks[item.task.id] ? " done" : ""}`} key={item.task.id}>
        <input
          type="checkbox"
          checked={!!checks[item.task.id]}
          onChange={(e) => setChecks((c) => ({ ...c, [item.task.id]: e.target.checked }))}
        />
        <div>
          <div className="kh-t">{item.task.title}</div>
          <div className="kh-d">{item.task.desc}</div>
        </div>
      </label>
    ) : (
      <React.Fragment key={i}>{item.node}</React.Fragment>
    );

  const renderSection = (sec) => {
    let groups = DATA[sec.id];
    if (q) {
      groups = groups
        .map((g) => ({
          group: g.group,
          items: g.items.filter((i) => `${i.text} ${g.group} ${sec.label}`.toLowerCase().includes(q)),
        }))
        .filter((g) => g.items.length);
    }
    if (!groups.length) return null;
    const grid = sec.id === "journey" || sec.id === "answers";
    return (
      <div className="kh-sec" key={sec.id}>
        <div className="kh-sec-head">
          <div className="kh-sec-title">{sec.label}</div>
          <div className="kh-sec-blurb">{sec.blurb}</div>
        </div>
        {groups.map((g) => (
          <React.Fragment key={g.group}>
            <div className="kh-group">{g.group}</div>
            <div className={grid ? "kh-grid" : "kh-stack"}>{g.items.map(renderItem)}</div>
          </React.Fragment>
        ))}
      </div>
    );
  };

  const results = q ? SECTIONS.map(renderSection).filter(Boolean) : null;
  const done = DAILY_TASKS.filter((k) => checks[k]).length;

  return (
    <div className="page active">
      <div className="page-title-row">
        <div>
          <h2 className="page-title">Knowledge Hub</h2>
          <div className="page-desc">Everything you need to learn the dashboard and answer clients, in four short sections.</div>
        </div>
      </div>

      <div className="kh-top">
        <div className="kh-tabs" role="tablist">
          {SECTIONS.map((s) => (
            <button
              type="button"
              role="tab"
              aria-selected={!q && tab === s.id}
              className={`kh-tab${!q && tab === s.id ? " active" : ""}`}
              key={s.id}
              onClick={() => {
                setTab(s.id);
                setQuery("");
              }}
            >
              {s.label}
            </button>
          ))}
        </div>
        <input
          type="search"
          className="kh-search"
          aria-label="Search the hub"
          placeholder="Search the hub..."
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
      </div>

      {!q && tab === "daily" ? <div className="kh-progress">{done} of {DAILY_TASKS.length} done today</div> : null}

      {q ? (
        results.length ? results : <div className="kh-empty">Nothing matches “{query}”.</div>
      ) : (
        renderSection(SECTIONS.find((s) => s.id === tab))
      )}
    </div>
  );
};
